import { useState, useEffect, useCallback } from 'react';
import type { ReactElement } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import { getToken } from '../auth/token-store';
import { ENDPOINTS } from '../api/endpoints';
import { TOS_VERSION } from '../legal/terms';
import { Spinner } from '../components/shared/Spinner';
import styles from './OAuthSignupPage.module.css';

type OAuthCompleteResponse = {
  readonly redirect_url?: string;
  readonly error?: string;
  readonly error_description?: string;
};

type FormView = 'signin' | 'signup' | 'confirm' | 'forgot' | 'reset';

const OAuthSignupPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const proxyState = searchParams.get('proxy_state');
  const auth = useAuth();
  const navigate = useNavigate();
  const isDirectAuth = !proxyState;

  const [view, setView] = useState<FormView>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const completeOAuth = useCallback(async (): Promise<void> => {
    if (!proxyState) return;

    const idToken = getToken('id_token');
    const accessToken = getToken('access_token');
    if (!idToken || !accessToken) return;

    setRedirecting(true);
    setError(null);

    try {
      const resp = await fetch(ENDPOINTS.oauthComplete, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proxy_state: proxyState, id_token: idToken, access_token: accessToken, refresh_token: getToken('refresh_token') }),
      });
      const data = (await resp.json()) as OAuthCompleteResponse;
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        setError(data.error_description ?? 'OAuth flow failed');
        setRedirecting(false);
      }
    } catch {
      setError('Failed to complete OAuth flow. Please try again.');
      setRedirecting(false);
    }
  }, [proxyState]);

  // If already authenticated: complete OAuth flow (if proxy_state) or redirect to dashboard (direct auth)
  useEffect(() => {
    if (auth.isAuthenticated && proxyState) {
      void completeOAuth();
    } else if (auth.isAuthenticated && isDirectAuth) {
      void navigate('/dashboard');
    }
  }, [auth.isAuthenticated, proxyState, isDirectAuth, completeOAuth, navigate]);

  if (redirecting) {
    return (
      <div className={styles.page}>
        <div className={styles.redirecting}>
          <Spinner />
          <div className={styles.redirectingText}>Completing authentication...</div>
        </div>
      </div>
    );
  }

  const handleSignIn = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const result = await auth.signIn(email, password);
      if (result.challengeName) {
        setError(`Authentication challenge: ${result.challengeName}`);
        setLoading(false);
        return;
      }
      // signIn sets tokens via auth context — useEffect handles redirect (OAuth or dashboard)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setLoading(false);
    }
  };

  const handleSignUp = async (): Promise<void> => {
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await auth.signUp(email, password, TOS_VERSION);
      auth.setPendingEmail(email);
      setSuccess('Account created. Check your email for a verification code.');
      setView('confirm');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await auth.confirm(code);
      setSuccess('Email verified. Signing you in...');
      // Auto sign-in after confirmation
      const result = await auth.signIn(email, password);
      if (result.challengeName) {
        setView('signin');
        setSuccess('Email verified. Please sign in.');
        setLoading(false);
        return;
      }
      // useEffect handles redirect (OAuth or dashboard)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setLoading(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    setError(null);
    try {
      await auth.resendCode();
      setSuccess('Verification code resent. Check your email.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  };

  const handleForgot = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await auth.forgot(email);
      setSuccess('Reset code sent. Check your email.');
      setView('reset');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await auth.reset(code, newPassword);
      setSuccess('Password reset. Please sign in.');
      setView('signin');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (newView: FormView): void => {
    setView(newView);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className={styles.page}>
      <h2 className={styles.heading}>Authenticate</h2>
      <p className={styles.subheading}>{isDirectAuth ? 'Sign in or create an account to manage your subscription' : 'Sign in or create an account to connect your agent'}</p>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      {view === 'signin' && (
        <>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${styles.tabActive}`}
              type="button"
              data-testid="auth-tab-signin"
            >
              Sign In
            </button>
            <button
              className={styles.tab}
              type="button"
              data-testid="auth-tab-signup"
              onClick={() => { switchView('signup'); }}
            >
              Sign Up
            </button>
          </div>
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signin-email">Email</label>
              <input
                id="signin-email"
                className={styles.input}
                type="email"
                placeholder="operator@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signin-password">Password</label>
              <input
                id="signin-password"
                className={styles.input}
                type="password"
                placeholder="min 12 characters"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); }}
              />
            </div>
            <button
              className={styles.submit}
              type="button"
              data-testid="auth-signin-submit"
              disabled={loading || !email || !password}
              onClick={() => { void handleSignIn(); }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className={styles.footer}>
              <button className={styles.link} type="button" onClick={() => { switchView('forgot'); }}>
                Forgot password?
              </button>
            </div>
          </div>
        </>
      )}

      {view === 'signup' && (
        <>
          <div className={styles.tabs}>
            <button
              className={styles.tab}
              type="button"
              data-testid="auth-tab-signin"
              onClick={() => { switchView('signin'); }}
            >
              Sign In
            </button>
            <button
              className={`${styles.tab} ${styles.tabActive}`}
              type="button"
              data-testid="auth-tab-signup"
            >
              Sign Up
            </button>
          </div>
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                className={styles.input}
                type="email"
                placeholder="operator@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                className={styles.input}
                type="password"
                placeholder="min 12 characters"
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); }}
              />
              <span className={styles.hint}>12 characters minimum</span>
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signup-confirm">Confirm Password</label>
              <input
                id="signup-confirm"
                className={styles.input}
                type="password"
                placeholder="re-enter password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); }}
              />
            </div>
            <label className={styles.consent} htmlFor="signup-tos">
              <input
                id="signup-tos"
                data-testid="auth-tos"
                type="checkbox"
                checked={tosAccepted}
                onChange={(e) => { setTosAccepted(e.target.checked); }}
              />
              <span>
                I agree to the <Link to="/terms">Terms of Service</Link> and acknowledge the{' '}
                <Link to="/privacy">Privacy Policy</Link>
              </span>
            </label>
            <button
              className={styles.submit}
              type="button"
              data-testid="auth-signup-submit"
              disabled={loading || !email || !password || !confirmPassword || !tosAccepted}
              onClick={() => { void handleSignUp(); }}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>
        </>
      )}

      {view === 'confirm' && (
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirm-code">Verification Code</label>
            <input
              id="confirm-code"
              className={styles.input}
              type="text"
              placeholder="check your email"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => { setCode(e.target.value); }}
            />
          </div>
          <button
            className={styles.submit}
            type="button"
            data-testid="auth-confirm-submit"
            disabled={loading || !code}
            onClick={() => { void handleConfirm(); }}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
          <div className={styles.footer}>
            <button className={styles.link} type="button" onClick={() => { void handleResend(); }}>
              Resend code
            </button>
          </div>
        </div>
      )}

      {view === 'forgot' && (
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="forgot-email">Email</label>
            <input
              id="forgot-email"
              className={styles.input}
              type="email"
              placeholder="operator@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
            />
          </div>
          <button
            className={styles.submit}
            type="button"
            disabled={loading || !email}
            onClick={() => { void handleForgot(); }}
          >
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
          <div className={styles.footer}>
            <button className={styles.link} type="button" onClick={() => { switchView('signin'); }}>
              Back to sign in
            </button>
          </div>
        </div>
      )}

      {view === 'reset' && (
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reset-code">Reset Code</label>
            <input
              id="reset-code"
              className={styles.input}
              type="text"
              placeholder="from your email"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => { setCode(e.target.value); }}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reset-password">New Password</label>
            <input
              id="reset-password"
              className={styles.input}
              type="password"
              placeholder="min 12 characters"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); }}
            />
          </div>
          <button
            className={styles.submit}
            type="button"
            disabled={loading || !code || !newPassword}
            onClick={() => { void handleReset(); }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
          <div className={styles.footer}>
            <button className={styles.link} type="button" onClick={() => { switchView('signin'); }}>
              Back to sign in
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { OAuthSignupPage };
